import React, { useState, useEffect } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Stack,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    FormLabel,
    RadioGroup,
    Radio,
    FormControlLabel,
    Checkbox
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { createLeave, updateLeave } from '../../../services/LeaveService'

const LeaveEditModal = ({ open, onClose, leave, onSave }) => {
    const [errors, setErrors] = useState({})
    const [responseMessage, setResponseMessage] = useState('')
    const [showResponseDialog, setShowResponseDialog] = useState(false)
    const [loading, setLoading] = useState(false)
    const [newLeave, setNewLeave] = useState({
        type: '',
        reason: '',
        remarks: '',
        start: null,
        end: null,
        halfDay: 'NONE',
        day: 'NONE',
        includeHalfDay: false
    })

    console.log('LeaveEditModal', leave)

    // Populate form when leave is passed
    useEffect(() => {
        if (leave) {
            setNewLeave((prev) => ({
                ...prev, // keep default structure
                ...leave,
                type: leave.type || '',
                reason: leave.reason || '',
                remarks: leave.remarks || '',
                start: leave.start ? new Date(leave.start) : null,
                end: leave.end ? new Date(leave.end) : null,
                halfDay: leave.halfDay == 'NONE' ? '' : leave.halfDay,
                day: leave.day == 'NONE' ? '' : leave.day,
                includeHalfDay: leave.halfDay == 'NONE' ? false : true
            }))
        }
    }, [leave])

    const validateFields = () => {
        const newErrors = {}
        const requiredFields = ['type', 'reason', 'start', 'remarks']

        if (!newLeave.includeHalfDay) {
            requiredFields.push('end')
        }

        requiredFields.forEach((field) => {
            if (!newLeave[field]) {
                newErrors[field] = 'This field is required'
            }
        })

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleRegisterInputChange = (event) => {
        const { name, value, checked } = event.target

        setNewLeave((prevLeave) => {
            let updatedLeave = { ...prevLeave, [name]: value }

            if (name === 'type') {
                updatedLeave.start = null
                updatedLeave.end = null
            }
            if (name === 'includeHalfDay') {
                updatedLeave.includeHalfDay = checked
                if (checked) {
                    updatedLeave.end = updatedLeave.start || null
                } else {
                    updatedLeave.halfDay = 'NONE'
                    updatedLeave.day = 'NONE'
                }
            }
            if (name === 'halfDay') {
                const [halfDay, day] = value.split('|')
                updatedLeave.halfDay = halfDay || 'NONE'
                updatedLeave.day = day || 'NONE'
            }

            return updatedLeave
        })
    }

    const handleFormSubmit = async (event) => {
        event.preventDefault()
        if (loading) return

        setLoading(true)

        if (validateFields()) {
            try {
                const payload = {
                    ...newLeave,
                    halfDay: newLeave.halfDay?.trim() ? newLeave.halfDay : 'NONE',
                    day: newLeave.day?.trim() ? newLeave.day : 'NONE'
                }
                const result = await updateLeave(payload)
                setResponseMessage(result.error || result.message)
                setShowResponseDialog(true)

                if (!result.error) onSave()
            } catch (error) {
                setResponseMessage('An error occurred while updating the leave.')
                setShowResponseDialog(true)
            } finally {
                setLoading(false)
                onClose() // Optionally close the modal after saving
            }
        } else {
            setLoading(false)
        }
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Leave</DialogTitle>
                <DialogContent>
                    <form onSubmit={handleFormSubmit}>
                        <Stack spacing={2}>
                            <FormControl fullWidth error={Boolean(errors.type)}>
                                <InputLabel>Leave Type</InputLabel>
                                <Select
                                    name="type"
                                    value={newLeave.type}
                                    onChange={handleRegisterInputChange}
                                    label="Leave Type"
                                >
                                    <MenuItem value="VACATION LEAVE">VACATION LEAVE</MenuItem>
                                    <MenuItem value="SICK LEAVE">SICK LEAVE</MenuItem>
                                    <MenuItem value="EMERGENCY LEAVE">EMERGENCY LEAVE</MenuItem>
                                    <MenuItem value="MATERNITY LEAVE">MATERNITY LEAVE</MenuItem>
                                    <MenuItem value="PATERNITY LEAVE">PATERNITY LEAVE</MenuItem>
                                    <MenuItem value="UNPAID LEAVE">ABSENT</MenuItem>
                                </Select>
                                {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
                            </FormControl>

                            <TextField
                                name="reason"
                                label="Reason"
                                fullWidth
                                value={newLeave.reason}
                                onChange={handleRegisterInputChange}
                                error={Boolean(errors.reason)}
                                helperText={errors.reason}
                            />

                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Start Date"
                                    value={newLeave.start}
                                    minDate={
                                        newLeave.type === 'VACATION LEAVE'
                                            ? new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
                                            : null
                                    }
                                    onChange={(start) =>
                                        handleRegisterInputChange({
                                            target: { name: 'start', value: start }
                                        })
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            error={Boolean(errors.start)}
                                            helperText={errors.start}
                                        />
                                    )}
                                />
                            </LocalizationProvider>

                            {!newLeave.includeHalfDay && (
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="End Date"
                                        value={newLeave.end}
                                        minDate={newLeave.start || new Date()}
                                        onChange={(end) =>
                                            handleRegisterInputChange({
                                                target: { name: 'end', value: end }
                                            })
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                error={Boolean(errors.end)}
                                                helperText={errors.end}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            )}

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={newLeave.includeHalfDay}
                                        onChange={handleRegisterInputChange}
                                        name="includeHalfDay"
                                    />
                                }
                                label="Include Half-Day Option"
                            />

                            {newLeave.includeHalfDay && (
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Half-Day Option</FormLabel>
                                    <RadioGroup
                                        name="halfDay"
                                        value={`${newLeave.halfDay || 'NONE'}|${newLeave.day || 'NONE'}`}
                                        onChange={handleRegisterInputChange}
                                    >
                                        <FormControlLabel value="AM|FD" control={<Radio />} label="Morning Half-Day" />
                                        <FormControlLabel
                                            value="PM|FD"
                                            control={<Radio />}
                                            label="Afternoon Half-Day"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            )}

                            <TextField
                                name="remarks"
                                label="Remarks"
                                fullWidth
                                multiline
                                minRows={4}
                                value={newLeave.remarks}
                                onChange={handleRegisterInputChange}
                                error={!!errors.remarks}
                                helperText={errors.remarks}
                            />
                        </Stack>

                        <DialogActions sx={{ mt: 2 }}>
                            <Button onClick={onClose} color="secondary">
                                Cancel
                            </Button>
                            <Button type="submit" color="primary" variant="contained" disabled={loading}>
                                {loading ? 'Saving...' : 'Save'}
                            </Button>
                        </DialogActions>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={showResponseDialog} onClose={() => setShowResponseDialog(false)}>
                <DialogTitle>Response</DialogTitle>
                <DialogContent>
                    <p>{responseMessage}</p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowResponseDialog(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default LeaveEditModal
